import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Wallet, Plus, Edit, Trash2, Check, X } from 'lucide-react';
import { getCmgBaremes, createCmgBareme, updateCmgBareme, deleteCmgBareme } from '../../api';
import { useToast } from '../../contexts/NotificationContext';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : null;

const CmgBaremeManagement = () => {
  const [baremes, setBaremes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ label: '', monthlyAmount: '', position: 0, validFrom: '', validUntil: '' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({ label: '', monthlyAmount: '', position: 0, validFrom: '', validUntil: '' });
  const toast = useToast();

  const fetchBaremes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCmgBaremes();
      setBaremes(response.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des baremes');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBaremes(); }, [fetchBaremes]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!formData.label.trim() || !formData.monthlyAmount) return;
    try {
      setLoading(true);
      setError(null);
      await createCmgBareme({
        label: formData.label,
        monthlyAmount: parseFloat(formData.monthlyAmount),
        position: parseInt(formData.position) || baremes.length + 1,
        validFrom: formData.validFrom || null,
        validUntil: formData.validUntil || null,
      });
      setFormData({ label: '', monthlyAmount: '', position: 0, validFrom: '', validUntil: '' });
      setShowForm(false);
      toast.success('Bareme CMG ajouté');
      fetchBaremes();
    } catch (err) {
      setError("Erreur lors de l'ajout");
      console.error(err);
      setLoading(false);
    }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setEditData({
      label: b.label,
      monthlyAmount: String(b.monthlyAmount),
      position: b.position,
      validFrom: b.validFrom ? b.validFrom.slice(0, 10) : '',
      validUntil: b.validUntil ? b.validUntil.slice(0, 10) : '',
    });
  };

  const cancelEdit = () => { setEditId(null); };

  const saveEdit = async (id) => {
    if (!editData.label.trim() || !editData.monthlyAmount) { cancelEdit(); return; }
    try {
      setLoading(true);
      setError(null);
      await updateCmgBareme(id, {
        label: editData.label,
        monthlyAmount: parseFloat(editData.monthlyAmount),
        position: parseInt(editData.position) || 0,
        validFrom: editData.validFrom || null,
        validUntil: editData.validUntil || null,
      });
      cancelEdit();
      toast.success('Bareme mis à jour');
      fetchBaremes();
    } catch (err) {
      setError('Erreur lors de la modification');
      setLoading(false);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Supprimer le bareme "${b.label}" ?`)) return;
    try {
      setLoading(true);
      setError(null);
      await deleteCmgBareme(b.id);
      toast.success('Bareme supprimé');
      fetchBaremes();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  if (loading && baremes.length === 0) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <Card><CardContent><p className="text-gray-500 py-8 text-center">Chargement...</p></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet size={20} />
            Barèmes CMG
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">{error}</div>
          )}

          <p className="text-sm text-gray-500 mb-4">
            Montants mensuels CMG par barème avec période de validité. Utilisés dans le simulateur de régularisation.
          </p>

          {showForm && (
            <form onSubmit={handleAdd} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  value={formData.label}
                  onChange={(e) => setFormData(f => ({ ...f, label: e.target.value }))}
                  placeholder="Label * (ex: CMG 4)"
                  required autoFocus
                />
                <input
                  type="number" step="0.01"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  value={formData.monthlyAmount}
                  onChange={(e) => setFormData(f => ({ ...f, monthlyAmount: e.target.value }))}
                  placeholder="Montant mensuel * (EUR)"
                  required
                />
                <input
                  type="number"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  value={formData.position}
                  onChange={(e) => setFormData(f => ({ ...f, position: e.target.value }))}
                  placeholder="Position (tri)"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Valide à partir du</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    value={formData.validFrom}
                    onChange={(e) => setFormData(f => ({ ...f, validFrom: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Valide jusqu'au</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(f => ({ ...f, validUntil: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" disabled={loading || !formData.label.trim() || !formData.monthlyAmount}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition disabled:opacity-50 text-sm">
                  <Check size={18} /> Créer
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-md transition text-sm">
                  Annuler
                </button>
              </div>
            </form>
          )}

          <div>
            <h3 className="font-semibold text-gray-700 mb-3">Barèmes ({baremes.length})</h3>
            {baremes.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">Aucun barème configuré.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 font-semibold text-gray-700">Label</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-700">Montant mensuel</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-700">Validité</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-700">Position</th>
                      <th className="text-center px-3 py-2 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baremes.map((b) => (
                      <tr key={b.id} className="border-t hover:bg-gray-50 transition">
                        <td className="px-3 py-2">
                          {editId === b.id ? (
                            <input type="text" className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                              value={editData.label}
                              onChange={(e) => setEditData(d => ({ ...d, label: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(b.id); if (e.key === 'Escape') cancelEdit(); }}
                              autoFocus />
                          ) : (
                            <span className="text-gray-800 font-medium">{b.label}</span>
                          )}
                        </td>
                        <td className="text-right px-3 py-2">
                          {editId === b.id ? (
                            <input type="number" step="0.01" className="w-28 px-2 py-1 border border-gray-300 rounded-md text-sm text-right"
                              value={editData.monthlyAmount}
                              onChange={(e) => setEditData(d => ({ ...d, monthlyAmount: e.target.value }))}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(b.id); if (e.key === 'Escape') cancelEdit(); }} />
                          ) : (
                            <span className="text-gray-800">{fmt(b.monthlyAmount)} €</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          {editId === b.id ? (
                            <div className="flex gap-1 justify-center">
                              <input type="date" className="w-32 px-2 py-1 border border-gray-300 rounded-md text-xs"
                                value={editData.validFrom}
                                onChange={(e) => setEditData(d => ({ ...d, validFrom: e.target.value }))} />
                              <span className="text-gray-400 self-center">→</span>
                              <input type="date" className="w-32 px-2 py-1 border border-gray-300 rounded-md text-xs"
                                value={editData.validUntil}
                                onChange={(e) => setEditData(d => ({ ...d, validUntil: e.target.value }))} />
                            </div>
                          ) : (
                            <span className="text-gray-500 text-xs whitespace-nowrap">
                              {b.validFrom || b.validUntil ? (
                                <>{fmtDate(b.validFrom) || '…'} → {fmtDate(b.validUntil) || '…'}</>
                              ) : (
                                <span className="text-gray-300">—</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          {editId === b.id ? (
                            <input type="number" className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm text-center"
                              value={editData.position}
                              onChange={(e) => setEditData(d => ({ ...d, position: e.target.value }))} />
                          ) : (
                            <span className="text-gray-500">{b.position}</span>
                          )}
                        </td>
                        <td className="text-center px-3 py-2">
                          <div className="flex justify-center gap-1">
                            {editId === b.id ? (
                              <>
                                <button onClick={() => saveEdit(b.id)} className="p-1 text-green-600 hover:bg-green-50 rounded-full" title="Enregistrer">
                                  <Check size={16} />
                                </button>
                                <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full" title="Annuler">
                                  <X size={16} />
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEdit(b)} className="p-1 text-primary hover:bg-primary/5 rounded-full" title="Modifier" disabled={loading}>
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => handleDelete(b)} className="p-1 text-red-600 hover:bg-red-50 rounded-full" title="Supprimer" disabled={loading}>
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      {!showForm && (
        <button onClick={() => setShowForm(true)}
          className="fixed bottom-6 right-6 z-50 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary-dark transition-all hover:scale-105"
          title="Ajouter un barème">
          <Plus size={24} />
        </button>
      )}
    </div>
  );
};

export default CmgBaremeManagement;
